import { el } from '../dom';

/**
 * Central pixel-art icon set + emoji replacement. The whole app renders game
 * concepts with sprites sliced from the Cute Fantasy pack instead of emoji.
 *
 * - `gicon(name)` → an <img> for a known icon.
 * - `richText(str)` → splits a string on any emoji: mapped emoji become sprite
 *   <img>s, UNMAPPED emoji are dropped. Spread into `el(...)` children.
 * - `stripEmoji(str)` → text-only contexts (toasts, titles): removes every emoji.
 *
 * Net effect: no emoji survives to the DOM — either it becomes a sprite or it is
 * removed.
 */

const C = '/sprites/ui/currency';
const A = '/sprites/ui/arena';
const I = '/sprites/ui/icons';

export const ICON = {
  coin: `${C}/coin.png`, shroom: `${C}/shroom.png`,
  // The FORAGED mushroom, which is not the currency. They were the same icon,
  // so an achievement for picking 250 mushrooms displayed the money token —
  // the same conflation that made buying energy read as spending shrooms.
  mushroom: '/sprites/icons/mushroom.png',
  swords: `${A}/swords.png`, star: `${A}/star.png`, check: `${A}/check.png`,
  heart: `${I}/heart.png`, energy: `${I}/energy.png`, crown: `${I}/crown.png`,
  trophy: `${I}/trophy.png`, sword: `${I}/sword.png`, chest: `${I}/chest.png`,
  gear: `${I}/gear.png`, xmark: `${I}/xmark.png`, shield: `${I}/shield.png`,
  axe: `${I}/axe.png`, pickaxe: `${I}/pickaxe.png`, hoe: `${I}/hoe.png`,
  hammer: `${I}/hammer.png`, water: `${I}/water.png`, fire: `${I}/fire.png`,
  wood: `${I}/wood.png`, stone: `${I}/stone.png`, ore: `${I}/ore.png`,
  gem: `${I}/gem.png`, leaf: `${I}/leaf.png`, seed: `${I}/seed.png`,
  helmet: `${I}/helmet.png`, chestplate: `${I}/chestplate.png`, legs: `${I}/legs.png`,
  // HUD chrome, sliced by scripts/build-ui-icons.py out of the pack's UI sheets.
  chat: `${I}/chat.png`, map: `${I}/map.png`,
  caretUp: `${I}/caret_up.png`, caretDown: `${I}/caret_down.png`,
  caretLeft: `${I}/caret_left.png`, caretRight: `${I}/caret_right.png`,
  // Wardrobe / appearance sprites. They were sliced with the rest of the set but
  // never registered, which is why the emoji they should have replaced were still
  // reaching the screen (a 🎩 rendered as a system glyph next to pixel art).
  hat: `${I}/hat.png`, shirt: `${I}/shirt.png`, boots: `${I}/boots.png`,
  hair: `${I}/hair.png`, face: `${I}/face.png`, person: `${I}/person.png`,
  footprints: `${I}/footprints.png`, dice: `${I}/dice.png`,
} as const;

export type IconName = keyof typeof ICON;

/** Emoji → icon path. Anything not here is stripped by richText/stripEmoji. */
const EMOJI2ICON: Record<string, string> = {
  '🪙': ICON.coin, '💰': ICON.coin,
  '🍄': ICON.shroom,
  '🪵': ICON.wood, '🪚': ICON.wood,
  '🪨': ICON.stone,
  '⛏': ICON.pickaxe, '⛏️': ICON.pickaxe,
  '⚒': ICON.hammer, '⚒️': ICON.hammer, '🔨': ICON.hammer, '🏗': ICON.hammer, '🏗️': ICON.hammer,
  '🪓': ICON.axe,
  '💎': ICON.gem,
  '🌱': ICON.seed,
  '🌾': ICON.leaf, '🌿': ICON.leaf, '🌳': ICON.leaf,
  '💧': ICON.water,
  '🔥': ICON.fire,
  '⚡': ICON.energy, '⚡️': ICON.energy,
  '❤': ICON.heart, '❤️': ICON.heart,
  '⚔': ICON.swords, '⚔️': ICON.swords, '🗡': ICON.sword, '🗡️': ICON.sword,
  '🏆': ICON.trophy, '🏅': ICON.trophy, '🥇': ICON.trophy, '👑': ICON.crown,
  '⭐': ICON.star, '★': ICON.star, '✨': ICON.star,
  '✓': ICON.check, '✅': ICON.check, '☑': ICON.check,
  '✗': ICON.xmark, '❌': ICON.xmark, '✕': ICON.xmark, '✖': ICON.xmark,
  '🛡': ICON.shield, '🛡️': ICON.shield, '🪖': ICON.helmet, '👖': ICON.legs,
  // Glyphs that HAD a sprite all along but no mapping, so richText dropped them and
  // the copy lost its icon (⚙️ next to "Settings" was the visible symptom).
  '⚙': ICON.gear, '⚙️': ICON.gear,
  '🎩': ICON.hat, '👕': ICON.shirt, '🥾': ICON.boots, '👟': ICON.boots,
  '💇': ICON.hair, '🧍': ICON.person, '🚶': ICON.footprints, '🎲': ICON.dice,
  // Category glyphs with no exact sprite: mapped to the nearest true pack icon
  // rather than left to vanish. Arena → crossed swords, dedication → a chest of
  // hoarded finds, exploration → the map. "Hidden" reuses the gear emblem the
  // achievements list already gives that category — leaving it unmapped rendered
  // one heading icon-less next to seven that had sprites.
  '🏟': ICON.swords, '🏟️': ICON.swords, '📚': ICON.chest, '🧭': ICON.map,
  '❓': ICON.gear, '❔': ICON.gear,
  '🛟': ICON.shield,
};

/** Matches pictographic emoji + the specific dingbat symbols we use (★ ✓ ✗ …),
 *  with an optional emoji variation selector. Deliberately excludes plain arrows
 *  (→ ↔ ⬅) so decorative arrows in copy survive. */
const EMOJI_RE = /(?:\p{Extended_Pictographic}|[★☆✓✔✗✘✕✖✎])️?/gu;

/** One inline icon <img>, crisp, baseline-aligned with adjacent text. */
export function gicon(name: IconName, h = 14): HTMLImageElement {
  return el('img', 'inline-block align-[-0.18em] shrink-0', {
    src: ICON[name], alt: '', style: `height:${h}px;width:auto;image-rendering:pixelated`,
  });
}

function iconImgFor(src: string, h: number): HTMLImageElement {
  return el('img', 'inline-block align-[-0.18em] shrink-0', {
    src, alt: '', style: `height:${h}px;width:auto;image-rendering:pixelated`,
  });
}

/**
 * Turn a string with emoji into DOM nodes: mapped emoji → sprite <img>, unmapped
 * emoji → removed. Returns an array to spread into `el(tag, cls, {}, ...richText(s))`.
 */
export function richText(str: string, h = 14): (string | HTMLElement)[] {
  const out: (string | HTMLElement)[] = [];
  let last = 0;
  for (const m of str.matchAll(EMOJI_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(str.slice(last, idx));
    const bare = m[0].replace(/️/g, '');
    const path = EMOJI2ICON[m[0]] ?? EMOJI2ICON[bare];
    if (path) out.push(iconImgFor(path, h));
    last = idx + m[0].length;
  }
  if (last < str.length) out.push(str.slice(last));
  // Collapse any double spaces left where an unmapped emoji was dropped.
  return out.map((n) => (typeof n === 'string' ? n.replace(/ {2,}/g, ' ') : n));
}

/** Remove every emoji from a string (text-only contexts like toasts/titles). */
export function stripEmoji(str: string): string {
  return str.replace(EMOJI_RE, '').replace(/\s{2,}/g, ' ').trim();
}
